import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

type BlockFormat = 'p' | 'h1' | 'h2' | 'h3' | 'blockquote';

interface ToolbarOption<T extends string> {
  label: string;
  value: T;
}

@Component({
  selector: 'app-rich-text-editor',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './rich-text-editor.component.html',
  styleUrl: './rich-text-editor.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true,
    },
  ],
})
export class RichTextEditorComponent implements ControlValueAccessor, AfterViewInit {
  @Input() label = 'Editeur';
  @Input() placeholder = 'Ecrire ici...';
  @Input() disabled = false;

  @Output() valueChange = new EventEmitter<string>();

  @ViewChild('editor', { static: true }) private readonly editorRef!: ElementRef<HTMLDivElement>;
  @ViewChild('imageInput', { static: true }) private readonly imageInputRef!: ElementRef<HTMLInputElement>;

  protected readonly blockFormats: ToolbarOption<BlockFormat>[] = [
    { label: 'Paragraphe', value: 'p' },
    { label: 'Titre 1', value: 'h1' },
    { label: 'Titre 2', value: 'h2' },
    { label: 'Titre 3', value: 'h3' },
    { label: 'Citation', value: 'blockquote' },
  ];

  protected currentBlockFormat: BlockFormat = 'p';
  protected currentFontSizePx = 16;
  protected value = '';
  protected cellAlignMenuOpen = false;
  protected cellAlignMenuX = 0;
  protected cellAlignMenuY = 0;
  protected tableDialogOpen = false;
  protected tableDialogMode: 'insert' | 'update' = 'insert';
  protected tableRowsInput = 2;
  protected tableColsInput = 2;
  protected tableBorderColor = '#ffffff';
  protected tableBorderWidthPx = 1;
  protected tableShowBorders = false;

  private pendingValue = '';
  private savedRange: Range | null = null;
  private selectedTableCell: HTMLTableCellElement | null = null;
  private selectedTableElement: HTMLTableElement | null = null;
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(private cdr: ChangeDetectorRef) {}

  writeValue(value: string | null): void {
    this.pendingValue = value ?? '';
    this.value = this.pendingValue;

    if (this.editorRef) {
      this.editorRef.nativeElement.innerHTML = this.pendingValue;
      // Setup resize handles after loading content
      setTimeout(() => this.setupImageResizeHandles(), 0);
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.editorRef) {
      this.editorRef.nativeElement.contentEditable = String(!isDisabled);
    }
  }

  ngAfterViewInit(): void {
    this.editorRef.nativeElement.innerHTML = this.pendingValue;
    this.editorRef.nativeElement.contentEditable = String(!this.disabled);
    this.syncToolbarState();
    this.setupImageResizeHandles();
  }

  protected handleInput(): void {
    this.commitCurrentValue();
  }

  protected handleBlur(): void {
    this.onTouched();
  }

  protected handleEditorContextMenu(event: MouseEvent): void {
    if (this.disabled) {
      return;
    }

    const cell = this.findTableCellFromEvent(event);
    if (!cell || !this.editorRef.nativeElement.contains(cell)) {
      this.closeCellAlignMenu();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.selectedTableCell = cell;
    this.selectedTableElement = cell.closest('table');
    this.cellAlignMenuX = event.clientX;
    this.cellAlignMenuY = event.clientY;
    this.cellAlignMenuOpen = true;
  }

  private findTableCellFromEvent(event: MouseEvent): HTMLTableCellElement | null {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    for (const item of path) {
      if (item instanceof HTMLTableCellElement) {
        return item;
      }
      if (item instanceof HTMLElement) {
        const found = item.closest('td, th');
        if (found instanceof HTMLTableCellElement) {
          return found;
        }
      }
    }

    const rawTarget = event.target;
    const elementTarget = rawTarget instanceof Element
      ? rawTarget
      : rawTarget instanceof Node
        ? rawTarget.parentElement
        : null;
    const fallback = elementTarget?.closest('td, th');
    return fallback instanceof HTMLTableCellElement ? fallback : null;
  }

  protected setCellVerticalAlign(align: 'top' | 'middle' | 'bottom'): void {
    if (!this.selectedTableCell) {
      this.closeCellAlignMenu();
      return;
    }

    this.selectedTableCell.style.verticalAlign = align;
    this.selectedTableCell.setAttribute('valign', align === 'middle' ? 'middle' : align);
    this.commitCurrentValue();
    this.closeCellAlignMenu();
    this.cdr.detectChanges();
  }

  protected keepCellAlignMenuOpen(event: MouseEvent): void {
    event.stopPropagation();
  }

  @HostListener('document:keydown.escape')
  protected onEscapePressed(): void {
    this.closeCellAlignMenu();
    this.cancelInsertTable();
  }

  protected captureSelection(): void {
    this.rememberSelection();
    this.syncToolbarState();
  }

  protected rememberSelection(): void {
    const selection = document.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);
    if (!this.editorRef.nativeElement.contains(range.commonAncestorContainer)) {
      return;
    }

    this.savedRange = range.cloneRange();
  }

  protected preventToolbarFocus(event: MouseEvent): void {
    event.preventDefault();
    this.captureSelection();
  }

  protected applyCommand(command: string, value?: string): void {
    if (this.disabled) {
      return;
    }

    this.restoreSelection();
    this.focusEditor();
    document.execCommand(command, false, value);
    this.commitCurrentValue();
  }

  protected applyBlockFormat(value: string): void {
    if (this.disabled) {
      return;
    }

    const block = value as BlockFormat;
    this.currentBlockFormat = block;
    this.restoreSelection();
    this.focusEditor();
    document.execCommand('formatBlock', false, `<${block}>`);
    this.commitCurrentValue();
  }

  protected applyFontSizePx(value: string): void {
    if (this.disabled) {
      return;
    }

    const normalizedValue = value.trim();
    const parsed = normalizedValue === '' ? 16 : Number(normalizedValue);
    if (!Number.isFinite(parsed)) {
      return;
    }

    const sizePx = Math.max(8, Math.min(72, Math.round(parsed)));
    this.currentFontSizePx = sizePx;

    if (!this.savedRange || this.savedRange.collapsed) {
      return;
    }

    this.restoreSelection();
    this.focusEditor();

    const selection = document.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);
    if (!this.editorRef.nativeElement.contains(range.commonAncestorContainer) || range.collapsed) {
      return;
    }

    const styledWrapper = document.createElement('span');
    styledWrapper.style.fontSize = `${sizePx}px`;

    const fragment = range.extractContents();
    styledWrapper.appendChild(fragment);
    range.insertNode(styledWrapper);

    const newRange = document.createRange();
    newRange.selectNodeContents(styledWrapper);
    selection.removeAllRanges();
    selection.addRange(newRange);

    this.savedRange = newRange.cloneRange();

    this.commitCurrentValue();
  }

  protected undo(): void {
    this.applyCommand('undo');
  }

  protected redo(): void {
    this.applyCommand('redo');
  }

  protected insertOrderedList(): void {
    this.applyCommand('insertOrderedList');
  }

  protected insertUnorderedList(): void {
    this.applyCommand('insertUnorderedList');
  }

  protected alignLeft(): void {
    this.applyCommand('justifyLeft');
  }

  protected alignCenter(): void {
    this.applyCommand('justifyCenter');
  }

  protected alignRight(): void {
    this.applyCommand('justifyRight');
  }

  protected insertLink(): void {
    if (this.disabled) {
      return;
    }

    const rawUrl = window.prompt('Adresse du lien');
    if (!rawUrl) {
      return;
    }

    const normalizedUrl = this.normalizeUrl(rawUrl);
    this.restoreSelection();
    this.focusEditor();
    document.execCommand('createLink', false, normalizedUrl);
    this.commitCurrentValue();
  }

  protected insertTable(): void {
    if (this.disabled) {
      return;
    }

    this.tableDialogMode = 'insert';
    this.tableRowsInput = 2;
    this.tableColsInput = 2;
    this.tableBorderColor = '#ffffff';
    this.tableBorderWidthPx = 1;
    this.tableShowBorders = false;
    this.tableDialogOpen = true;
  }

  protected openTablePropertiesDialog(): void {
    if (this.disabled || !this.selectedTableElement) {
      this.closeCellAlignMenu();
      return;
    }

    const rows = Array.from(this.selectedTableElement.querySelectorAll('tr'));
    const maxCols = rows.reduce((acc, row) => Math.max(acc, row.querySelectorAll('td, th').length), 0);

    this.tableRowsInput = Math.max(1, rows.length || 1);
    this.tableColsInput = Math.max(1, maxCols || 1);

    const firstCell = this.selectedTableElement.querySelector('td, th') as HTMLTableCellElement | null;
    if (firstCell) {
      // Try multiple approaches to extract border properties
      const borderWidth = firstCell.style.borderWidth || '';
      const borderColor = firstCell.style.borderColor || '';
      const borderStyle = firstCell.style.borderStyle || '';
      
      // If individual properties are set, use them
      if (borderWidth && borderColor && borderStyle) {
        this.tableShowBorders = true;
        this.tableBorderWidthPx = Number(borderWidth.replace(/\D/g, '')) || 1;
        this.tableBorderColor = this.normalizeColorToHex(borderColor);
      } else {
        // Fallback: try to parse the shorthand border property
        const border = firstCell.style.border || '';
        const borderMatch = border.match(/(\d+)px\s+solid\s+([^;)]+)/i);
        if (borderMatch) {
          this.tableShowBorders = true;
          this.tableBorderWidthPx = Number(borderMatch[1]) || 1;
          this.tableBorderColor = this.normalizeColorToHex(borderMatch[2].trim());
        } else {
          // Also try parsing from style attribute string
          const styleAttr = firstCell.getAttribute('style') || '';
          const styleMatch = styleAttr.match(/border:\s*(\d+)px\s+solid\s+([^;]+)/i);
          if (styleMatch) {
            this.tableShowBorders = true;
            this.tableBorderWidthPx = Number(styleMatch[1]) || 1;
            this.tableBorderColor = this.normalizeColorToHex(styleMatch[2].trim());
          } else {
            this.tableShowBorders = false;
            this.tableBorderWidthPx = 1;
            this.tableBorderColor = '#ffffff';
          }
        }
      }
    } else {
      this.tableShowBorders = false;
      this.tableBorderWidthPx = 1;
      this.tableBorderColor = '#ffffff';
    }

    this.tableDialogMode = 'update';
    this.tableDialogOpen = true;
    this.closeCellAlignMenu();
  }

  protected deleteSelectedTable(): void {
    if (this.disabled || !this.selectedTableElement) {
      this.closeCellAlignMenu();
      return;
    }

    this.selectedTableElement.remove();
    this.commitCurrentValue();
    this.closeCellAlignMenu();
  }

  protected confirmInsertTable(): void {
    if (this.disabled) {
      return;
    }

    const rows = this.normalizeTableDimension(String(this.tableRowsInput));
    const cols = this.normalizeTableDimension(String(this.tableColsInput));
    if (!rows || !cols) {
      window.alert('Veuillez saisir un nombre entier entre 1 et 20.');
      return;
    }

    const borderWidth = Math.max(1, Math.min(20, Math.round(Number(this.tableBorderWidthPx) || 1)));
    const borderColor = /^#[0-9a-f]{6}$/i.test(this.tableBorderColor) ? this.tableBorderColor : '#ffffff';
    const borderStyle = this.tableShowBorders
      ? `border:${borderWidth}px solid ${borderColor};`
      : '';
    const cellStyle = `${borderStyle}min-width:50px;min-height:50px;height:50px;`;

    if (this.tableDialogMode === 'update' && this.selectedTableElement) {
      const oldRows = Array.from(this.selectedTableElement.querySelectorAll('tr'));
      const oldCells = oldRows.map((row) => Array.from(row.querySelectorAll('td, th')).map((c) => c.innerHTML));
      const tableRows = Array.from({ length: rows }, (_, rowIndex) => {
        const cells = Array.from({ length: cols }, (_, colIndex) => {
          const content = oldCells[rowIndex]?.[colIndex] ?? '&nbsp;';
          return `<td style="${cellStyle}">${content || '&nbsp;'}</td>`;
        }).join('');
        return `<tr>${cells}</tr>`;
      }).join('');

      this.selectedTableElement.classList.add('rt-table');
      this.selectedTableElement.setAttribute('style', 'border-collapse:collapse;');
      this.selectedTableElement.innerHTML = `<tbody>${tableRows}</tbody>`;
      this.commitCurrentValue();
    } else {
      const tableRows = Array.from({ length: rows }, () => {
        const cells = Array.from({ length: cols }, () => `<td style="${cellStyle}">&nbsp;</td>`).join('');
        return `<tr>${cells}</tr>`;
      }).join('');

      this.insertHtml(`<table class="rt-table" style="border-collapse:collapse;"><tbody>${tableRows}</tbody></table>`);
    }

    this.tableDialogOpen = false;
    this.tableDialogMode = 'insert';
  }

  protected cancelInsertTable(): void {
    this.tableDialogOpen = false;
    this.tableDialogMode = 'insert';
  }

  protected triggerImagePicker(): void {
    if (this.disabled) {
      return;
    }

    this.imageInputRef.nativeElement.value = '';
    this.imageInputRef.nativeElement.click();
  }

  protected async handleImageSelection(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      window.alert('Sélectionnez un fichier image valide.');
      return;
    }

    let dataUrl = '';
    try {
      dataUrl = await this.prepareImageForInsert(file);
    } catch {
      // Fallback: insert original image if preprocessing fails.
      dataUrl = await this.readFileAsDataUrl(file);
    }

    const altText = this.escapeHtml(file.name.replace(/\.[^.]+$/, '') || 'Image');
    this.insertHtml(`<img class="rt-image" src="${dataUrl}" alt="${altText}" />`);
    // Setup resize handles for the newly inserted image
    this.setupImageResizeHandles();
  }

  protected isCommandActive(command: string): boolean {
    try {
      return document.queryCommandState(command);
    } catch {
      return false;
    }
  }

  private insertHtml(html: string): void {
    this.restoreSelection();
    this.focusEditor();
    document.execCommand('insertHTML', false, html);
    this.commitCurrentValue();
  }

  private commitCurrentValue(): void {
    // Get clean HTML without wrappers by cloning the editor content
    const editorClone = this.editorRef.nativeElement.cloneNode(true) as HTMLElement;
    
    // Remove all wrappers from the clone
    const wrappersToRemove = editorClone.querySelectorAll('.rt-image-wrapper') as NodeListOf<HTMLElement>;
    Array.from(wrappersToRemove).forEach((wrapper) => {
      const img = wrapper.querySelector('img.rt-image') as HTMLImageElement;
      if (img && wrapper.parentNode) {
        wrapper.parentNode.insertBefore(img, wrapper);
      }
      wrapper.remove();
    });

    // Save the clean HTML
    const html = editorClone.innerHTML;
    this.value = html;
    this.onChange(html);
    this.valueChange.emit(html);
    this.syncToolbarState();
    
    // Recreate handles after saving
    setTimeout(() => {
      this.setupImageResizeHandles();
      this.cdr.markForCheck();
    }, 0);
  }

  private setupImageResizeHandles(): void {
    const editor = this.editorRef.nativeElement;

    // Always start from a clean DOM state: unwrap all previous wrappers.
    const existingWrappers = Array.from(
      editor.querySelectorAll('.rt-image-wrapper') as NodeListOf<HTMLElement>
    );
    existingWrappers.forEach((wrapper) => {
      const img = wrapper.querySelector('img') as HTMLImageElement | null;
      if (img && wrapper.parentNode) {
        wrapper.parentNode.insertBefore(img, wrapper);
      }
      wrapper.remove();
    });

    const images = Array.from(editor.querySelectorAll('img') as NodeListOf<HTMLImageElement>);
    images.forEach((img: HTMLImageElement) => {
      img.classList.add('rt-image');

      const wrapper = document.createElement('span');
      wrapper.className = 'rt-image-wrapper';
      wrapper.style.display = 'inline-block';
      wrapper.style.position = 'relative';
      wrapper.style.overflow = 'visible';
      wrapper.style.verticalAlign = 'top';

      img.parentNode?.insertBefore(wrapper, img);
      wrapper.appendChild(img);

      this.ensureResizeHandles(wrapper, img);
    });
  }

  private ensureResizeHandles(wrapper: HTMLElement, img: HTMLImageElement): void {
    const existingHandles = wrapper.querySelectorAll('.rt-image-handle');
    existingHandles.forEach((handle) => handle.remove());

    ['nw', 'ne', 'sw', 'se'].forEach((corner) => {
      const handle = document.createElement('span');
      handle.className = `rt-image-handle ${corner}`;
      handle.style.position = 'absolute';
      handle.style.width = '12px';
      handle.style.height = '12px';
      handle.style.background = 'rgba(115, 216, 255, 0.92)';
      handle.style.border = '2px solid #fff';
      handle.style.borderRadius = '50%';
      handle.style.opacity = '1';
      handle.style.zIndex = '1000';
      handle.style.boxSizing = 'border-box';
      handle.style.pointerEvents = 'auto';

      if (corner === 'nw') {
        handle.style.top = '-6px';
        handle.style.left = '-6px';
        handle.style.cursor = 'nw-resize';
      } else if (corner === 'ne') {
        handle.style.top = '-6px';
        handle.style.right = '-6px';
        handle.style.cursor = 'ne-resize';
      } else if (corner === 'sw') {
        handle.style.bottom = '-6px';
        handle.style.left = '-6px';
        handle.style.cursor = 'sw-resize';
      } else {
        handle.style.bottom = '-6px';
        handle.style.right = '-6px';
        handle.style.cursor = 'se-resize';
      }

      handle.setAttribute('contenteditable', 'false');
      handle.addEventListener('mousedown', (e) => this.onImageResizeStart(e, img, corner));
      wrapper.appendChild(handle);
    });
  }

  private onImageResizeStart(event: MouseEvent, img: HTMLImageElement, corner: string): void {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startY = event.clientY;
    const startWidth = img.offsetWidth;
    const startHeight = img.offsetHeight;
    const ratio = startWidth / startHeight;

    const onMouseMove = (moveEvent: MouseEvent) => {
      let newWidth = startWidth;
      let newHeight = startHeight;

      if (corner.includes('e')) {
        newWidth = Math.max(50, startWidth + (moveEvent.clientX - startX));
      } else if (corner.includes('w')) {
        newWidth = Math.max(50, startWidth - (moveEvent.clientX - startX));
      }

      if (corner.includes('s')) {
        newHeight = Math.max(50, startHeight + (moveEvent.clientY - startY));
      } else if (corner.includes('n')) {
        newHeight = Math.max(50, startHeight - (moveEvent.clientY - startY));
      }

      const widthRatio = newWidth / startWidth;
      const heightRatio = newHeight / startHeight;
      const useRatio = Math.min(widthRatio, heightRatio);

      newWidth = Math.round(startWidth * useRatio);
      newHeight = Math.round(startHeight * useRatio);

      img.style.width = `${newWidth}px`;
      img.style.height = `${newHeight}px`;
      img.classList.add('resizing');
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      img.classList.remove('resizing');
      this.commitCurrentValue();
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  private focusEditor(): void {
    this.editorRef.nativeElement.focus();
  }

  private restoreSelection(): void {
    if (!this.savedRange) {
      return;
    }

    const selection = document.getSelection();
    if (!selection) {
      return;
    }

    selection.removeAllRanges();
    selection.addRange(this.savedRange);
  }

  closeCellAlignMenu(): void {
    this.cellAlignMenuOpen = false;
    this.selectedTableCell = null;
  }

  private normalizeColorToHex(color: string): string {
    const trimmed = color.trim();
    if (/^#[0-9a-f]{6}$/i.test(trimmed)) {
      return trimmed;
    }

    const context = document.createElement('canvas').getContext('2d');
    if (!context) {
      return '#ffffff';
    }

    context.fillStyle = '#ffffff';
    context.fillStyle = trimmed;
    const normalized = context.fillStyle;

    if (/^#[0-9a-f]{6}$/i.test(normalized)) {
      return normalized;
    }

    const rgbMatch = normalized.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/i);
    if (!rgbMatch) {
      return '#ffffff';
    }

    const r = Number(rgbMatch[1]).toString(16).padStart(2, '0');
    const g = Number(rgbMatch[2]).toString(16).padStart(2, '0');
    const b = Number(rgbMatch[3]).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  }

  private normalizeTableDimension(value: string): number | null {
    const parsed = Number(value);
    if (!Number.isInteger(parsed)) {
      return null;
    }

    const normalized = Math.max(1, Math.min(20, parsed));
    return normalized;
  }

  private syncToolbarState(): void {
    try {
      const selection = document.getSelection();
      if (!selection || selection.rangeCount === 0) {
        return;
      }

      const range = selection.getRangeAt(0);
      const startNode = range.startContainer.nodeType === Node.ELEMENT_NODE
        ? (range.startContainer as HTMLElement)
        : range.startContainer.parentElement;

      if (!startNode) {
        return;
      }

      const blockTag = startNode.closest('h1, h2, h3, blockquote, p')?.tagName?.toLowerCase();
      if (blockTag === 'h1' || blockTag === 'h2' || blockTag === 'h3' || blockTag === 'blockquote' || blockTag === 'p') {
        this.currentBlockFormat = blockTag as BlockFormat;
      }
    } catch {
      // Best effort only.
    }
  }

  private normalizeUrl(url: string): string {
    const trimmed = url.trim();
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) {
      return trimmed;
    }

    return `https://${trimmed}`;
  }

  private escapeHtml(value: string): string {
    const element = document.createElement('div');
    element.textContent = value;
    return element.innerHTML;
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  }

  private async prepareImageForInsert(file: File): Promise<string> {
    const originalDataUrl = await this.readFileAsDataUrl(file);

    // Keep animated GIFs untouched.
    if (file.type === 'image/gif') {
      return originalDataUrl;
    }

    const orientation = file.type === 'image/jpeg' || file.type === 'image/jpg'
      ? this.getJpegExifOrientation(await this.readFileAsArrayBuffer(file))
      : 1;

    const image = await this.loadImage(originalDataUrl);
    return this.resizeAndOrientImage(image, orientation, file.type || 'image/jpeg', 600);
  }

  private loadImage(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Impossible de charger l\'image.'));
      img.src = dataUrl;
    });
  }

  private resizeAndOrientImage(
    image: HTMLImageElement,
    orientation: number,
    mimeType: string,
    maxDimension: number
  ): string {
    const srcWidth = image.naturalWidth || image.width;
    const srcHeight = image.naturalHeight || image.height;
    if (!srcWidth || !srcHeight) {
      throw new Error('Dimensions image invalides.');
    }

    const swapsAxes = orientation >= 5 && orientation <= 8;
    const orientedWidth = swapsAxes ? srcHeight : srcWidth;
    const orientedHeight = swapsAxes ? srcWidth : srcHeight;

    const scale = Math.min(1, maxDimension / Math.max(orientedWidth, orientedHeight));
    const drawWidth = Math.max(1, Math.round(srcWidth * scale));
    const drawHeight = Math.max(1, Math.round(srcHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = swapsAxes ? drawHeight : drawWidth;
    canvas.height = swapsAxes ? drawWidth : drawHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas indisponible.');
    }

    this.applyExifTransform(context, orientation, drawWidth, drawHeight);
    context.drawImage(image, 0, 0, drawWidth, drawHeight);

    const outputType = /^image\/(jpeg|jpg|png|webp)$/i.test(mimeType) ? mimeType : 'image/jpeg';
    return canvas.toDataURL(outputType, outputType.includes('jpeg') || outputType.includes('jpg') ? 0.9 : undefined);
  }

  private applyExifTransform(
    context: CanvasRenderingContext2D,
    orientation: number,
    width: number,
    height: number
  ): void {
    switch (orientation) {
      case 2:
        context.transform(-1, 0, 0, 1, width, 0);
        break;
      case 3:
        context.transform(-1, 0, 0, -1, width, height);
        break;
      case 4:
        context.transform(1, 0, 0, -1, 0, height);
        break;
      case 5:
        context.transform(0, 1, 1, 0, 0, 0);
        break;
      case 6:
        context.transform(0, 1, -1, 0, height, 0);
        break;
      case 7:
        context.transform(0, -1, -1, 0, height, width);
        break;
      case 8:
        context.transform(0, -1, 1, 0, 0, width);
        break;
      default:
        // Orientation 1: no-op.
        break;
    }
  }

  private getJpegExifOrientation(buffer: ArrayBuffer): number {
    const view = new DataView(buffer);

    if (view.byteLength < 4 || view.getUint16(0, false) !== 0xFFD8) {
      return 1;
    }

    let offset = 2;
    while (offset + 4 <= view.byteLength) {
      const marker = view.getUint16(offset, false);
      offset += 2;

      if (marker === 0xFFE1) {
        if (offset + 2 > view.byteLength) {
          return 1;
        }
        const segmentLength = view.getUint16(offset, false);
        const segmentStart = offset + 2;
        if (segmentStart + segmentLength - 2 > view.byteLength) {
          return 1;
        }

        if (view.getUint32(segmentStart, false) !== 0x45786966) {
          return 1;
        }

        const tiffOffset = segmentStart + 6;
        if (tiffOffset + 8 > view.byteLength) {
          return 1;
        }

        const littleEndian = view.getUint16(tiffOffset, false) === 0x4949;
        const firstIfdOffset = view.getUint32(tiffOffset + 4, littleEndian);
        const ifdStart = tiffOffset + firstIfdOffset;
        if (ifdStart + 2 > view.byteLength) {
          return 1;
        }

        const entries = view.getUint16(ifdStart, littleEndian);
        for (let i = 0; i < entries; i += 1) {
          const entryOffset = ifdStart + 2 + (i * 12);
          if (entryOffset + 12 > view.byteLength) {
            return 1;
          }

          const tag = view.getUint16(entryOffset, littleEndian);
          if (tag === 0x0112) {
            const orientation = view.getUint16(entryOffset + 8, littleEndian);
            return orientation >= 1 && orientation <= 8 ? orientation : 1;
          }
        }

        return 1;
      }

      if ((marker & 0xFF00) !== 0xFF00 || marker === 0xFFDA || marker === 0xFFD9) {
        break;
      }

      if (offset + 2 > view.byteLength) {
        break;
      }

      const segmentLength = view.getUint16(offset, false);
      offset += segmentLength;
    }

    return 1;
  }
}